import React, { useEffect, useRef, useState, useMemo } from 'react'
import { styled } from 'linaria/react'
import { NavArrowDown } from 'iconoir-react'
import { useIntl } from '@jetshop/intl'
import { theme } from '../../Theme'
import { css } from 'linaria'

const CollapsibleCard = styled.article`
   transition: 0.3s;
   color: ${props => (props.useDarkText ? '#black' : '#fff')} !important;
`

const BottomContent = styled.div`
   margin-top: ${props => (props.isOpen ? '0.4rem' : '0.3rem')};
   display: flex;
   flex-direction: row;
   justify-content: flex-start;
   gap: 0.5rem;

   ${theme.below.md} {
      justify-content: center;
   }

   &:hover {
      opacity: 0.8;
   }
`

const ToggleMessage = styled.div`
   display: block;
   font-size: ${theme.font.size.m};
   font-weight: 300;
   font-family: var(--font-industry);
   cursor: pointer;
`

const TextContent = styled.div`
   font-size: ${theme.font.size.m};
   font-weight: 200;
   font-family: var(--font-industry);

   a {
      font-family: var(--font-industry);
      text-decoration: underline;
      font-weight: 700;
   }

   .hide {
      opacity: 0;
   }

   p {
      font-family: var(--font-industry);
      margin-block-start: 0px;
      margin-block-end: 0px;
   }

   ${theme.below.xl} {
      p {
         font-size: 0.95rem;  
         font-family: var(--font-industry);
         margin-block-start: 0px;
         margin-block-end: 0px;
      }
      h1 {
         font-family: var(--font-industry);
         font-size: 1.5rem;
      }
      h2 {
         font-family: var(--font-industry);
         font-size: 1.4rem;
      }
      h3 {
         font-family: var(--font-industry);
         font-size: 1.3rem;
      }
      h4 {
         font-family: var(--font-industry);
         font-size: 1.2rem;
      }
   }
`
const ToggleIcon = styled.span`
   justify-content: center;
   align-items: center;
   cursor: pointer;
   overflow: hidden;
   -moz-transition: all 0.15s linear;
   -webkit-transition: all 0.15s linear;
   transition: all 0.15s linear;

   &.rotate-center-down {
      -moz-transform: rotate(180deg);
      -webkit-transform: rotate(180deg);
      transform: rotate(180deg);
   }
   &.rotate-center-up {
      -moz-transform: rotate(360deg);
      -webkit-transform: rotate(360deg);
      transform: rotate(360deg);
   }
`
const lineClamp = css`
   display: -webkit-box;
   -webkit-line-clamp: 2;
   -webkit-box-orient: vertical;
   overflow: hidden;
`

const Collapsible = ({
   open,
   content,
   handleHeightAdapter,
   closedVisibleHeight,
   useDarkText,
}) => {
   const [isOpen, setIsOpen] = useState(open)
   const [contentHeight, setContentHeight] = useState(null)
   const refClampedDescription = useRef(null)
   const t = useIntl()

   const toggleHandler = () => {
      setIsOpen(prev => !prev)
   }

   useEffect(() => {
      setContentHeight(refClampedDescription.current.offsetHeight)
   }, [])

   useEffect(() => {
      if (!handleHeightAdapter) return
      if (isOpen) {
         handleHeightAdapter(contentHeight)
      } else {
         handleHeightAdapter(closedVisibleHeight)
      }
   }, [isOpen, contentHeight, handleHeightAdapter, closedVisibleHeight])

   const isClamped = useMemo(() => {
      if (!contentHeight) {
         return false
      }
      return !isOpen && contentHeight > closedVisibleHeight
   }, [isOpen, contentHeight, closedVisibleHeight])

   return (
      <>
         <CollapsibleCard
            className="collapsible-card-wrapper"
            useDarkText={useDarkText}
         >
            <TextContent
               ref={refClampedDescription}
               className={isClamped && lineClamp}
            >
               {content}
            </TextContent>
            {contentHeight && contentHeight > closedVisibleHeight && (
               <BottomContent isOpen={isOpen}>
                  <ToggleIcon
                     onClick={toggleHandler}
                     className={`${
                        isOpen ? 'rotate-center-down' : 'rotate-center-up'
                     }`}
                  >
                     <NavArrowDown strokeWidth={3} height="25px" width="25px" />
                  </ToggleIcon>
                  <ToggleMessage onClick={toggleHandler}>
                     {isOpen ? t('Show less content') : t('Show more content')}
                  </ToggleMessage>
               </BottomContent>
            )}
         </CollapsibleCard>
      </>
   )
}

export default Collapsible
